import React, { useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Link,
  List,
  ListItem,
  Popover,
  PopoverOrigin,
  Tooltip,
  Typography,
} from '@mui/material';
import { MarginHighlight } from '../../../../types';
import {
  GrossMarginHandlingGuidelineDoc,
  MarginHighlightActions,
  GrossMarginHighlightColor,
  NetMarginHighlightColor,
  NetMarginHighlightTitle,
} from '../../../../constants/marginTracker';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { titleCase } from '../../../../utils/titleCase';
import { Info } from '../Info/Info';
import {
  CustomIcon,
  IconName,
} from '../../../../components/customIcon/CustomIcon';

interface SummaryMarginProps {
  grossMargin: number;
  netMargin: number;
  newGrossMargin?: number;
  newNetMargin?: number;
  valueLineHeight?: string;
  grossMarginHighlights: MarginHighlight[];
  netMarginHighlights: MarginHighlight[];

  calculateMargins?: () => void;
}

export const Margin: React.FC<SummaryMarginProps> = ({
  grossMargin,
  grossMarginHighlights,
  netMargin,
  netMarginHighlights,
  newGrossMargin,
  newNetMargin,
  valueLineHeight,

  calculateMargins,
}) => {
  const marginAnchor = React.useRef<HTMLElement | null>(null);
  const [openPopover, setOpenPopover] = React.useState<boolean>(false);
  const [openNetMarginPopover, setOpenNetMarginPopover] =
    React.useState<boolean>(false);

  const [openGradientPopover, setOpenGradientPopover] =
    React.useState<boolean>(false);
  const [gradientMarginHover, setGradientMarginHover] =
    React.useState<MarginHighlight | null>(null);
  const gradientHover = React.useRef<HTMLElement | null>(null);

  const netMarginGradientHover = React.useRef<HTMLElement | null>(null);
  const [openNetMarginGradientPopover, setOpenNetMarginGradientPopover] =
    React.useState<boolean>(false);

  const hasNewGrossMargin = newGrossMargin != null;
  const hasNewNetMargin = newNetMargin != null;

  const {
    grossMarginHighlight,
    sortedGrossMarginHighlights,
    netMarginHighlight,
    sortedNetMarginHighlights,
  } = useMemo(() => {
    const marginHighlightIndex = grossMarginHighlights.findIndex(
      (marginHighlight) =>
        grossMargin != null &&
        (marginHighlight.min ?? -Infinity) <= grossMargin &&
        (marginHighlight.max ?? Infinity) >= grossMargin,
    );

    const sortedGrossMarginHighlights = Array.from(grossMarginHighlights).sort(
      (a, b) => (a.max ?? Infinity) - (b.max ?? Infinity),
    );

    const netMarginHighlightIndex = netMarginHighlights.findIndex(
      (marginHighlight) =>
        netMargin != null &&
        (marginHighlight.min ?? -Infinity) <= netMargin &&
        (marginHighlight.max ?? Infinity) >= netMargin,
    );

    const sortedNetMarginHighlights = Array.from(netMarginHighlights).sort(
      (a, b) => (a.max ?? Infinity) - (b.max ?? Infinity),
    );

    return {
      grossMarginHighlight: grossMarginHighlights[marginHighlightIndex] ?? {},
      sortedGrossMarginHighlights,
      netMarginHighlight: netMarginHighlights[netMarginHighlightIndex] ?? {},
      sortedNetMarginHighlights,
    };
  }, [grossMargin, grossMarginHighlights, netMargin, netMarginHighlights]);

  const grossMarginThemeColor: string =
    GrossMarginHighlightColor[grossMarginHighlight.type || ''] ??
    'text.primary';
  const netMarginThemeColor: string =
    NetMarginHighlightColor[netMarginHighlight.type || ''] ?? 'text.primary';

  const handlePopoverOpen = useCallback(() => {
    setOpenPopover(true);
  }, [setOpenPopover]);

  const handlePopoverClose = useCallback(() => {
    setOpenPopover(false);
  }, [setOpenPopover]);

  const handleNetMarginPopoverOpen = useCallback(() => {
    setOpenNetMarginPopover(true);
  }, []);

  const handleNetMarginPopoverClose = useCallback(() => {
    setOpenNetMarginPopover(false);
  }, []);

  const handleGradientPopoverOpen = useCallback(
    (hoveredBlock?: MarginHighlight, isNetMarginPopover?: boolean) => {
      if (hoveredBlock) {
        setGradientMarginHover(hoveredBlock);
      }
      if (isNetMarginPopover) setOpenNetMarginGradientPopover(true);
      else setOpenGradientPopover(true);
    },
    [setOpenGradientPopover, setGradientMarginHover],
  );

  const handleGradientPopoverClose = useCallback(
    (isNetMarginPopover?: boolean) => {
      if (isNetMarginPopover) setOpenNetMarginGradientPopover(false);
      else setOpenGradientPopover(false);
    },
    [setOpenGradientPopover],
  );

  return (
    <>
      <Box
        display="flex"
        gap="0.25rem"
        alignContent="center"
        alignItems="center"
      >
        <Info
          title={hasNewGrossMargin ? 'Gross margin old / new' : 'Gross margin'}
        >
          <Box
            ref={marginAnchor}
            padding="0.6rem 0.3rem"
            display="inline-flex"
            justifyContent="left"
            alignItems="center"
            borderRadius="0.5rem"
            minWidth="5.25rem"
            color={grossMarginThemeColor}
            onMouseEnter={hasNewGrossMargin ? undefined : handlePopoverOpen}
            onMouseLeave={hasNewGrossMargin ? undefined : handlePopoverClose}
            sx={{ cursor: 'default' }}
          >
            <MarginValues
              margin={grossMargin}
              newMargin={newGrossMargin}
              lineHeight={valueLineHeight}
              onZoneOpen={handlePopoverOpen}
              onZoneClose={handlePopoverClose}
            />
          </Box>

          <Box
            display="inline-flex"
            margin="0 0.375rem 0"
            borderRadius="0.125rem"
            height="0.25rem"
            sx={{
              '& :first-of-type(div)': {
                borderTopLeftRadius: '0.25rem',
                borderBottomLeftRadius: '0.25rem',
              },
              '& :last-of-type(div)': {
                borderTopRightRadius: '0.25rem',
                borderBottomRightRadius: '0.25rem',
              },
              backgroundImage: `linear-gradient(to right, ${sortedGrossMarginHighlights
                .map((smh) => GrossMarginHighlightColor[smh.type])
                .join(', ')})`,
              '&:hover': {
                transform: 'scaleY(1.5)',
                transition: 'all 0.15s ease-in-out',
              },
            }}
          >
            {sortedGrossMarginHighlights.map((smh) => (
              <Box
                ref={gradientHover}
                key={smh.type}
                flex="1"
                onMouseEnter={() => handleGradientPopoverOpen(smh)}
                onMouseLeave={() => handleGradientPopoverClose(false)}
              />
            ))}
          </Box>
        </Info>

        <MarginPopover
          anchorEl={marginAnchor}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          marginHighlight={grossMarginHighlight}
          open={openPopover}
          transformOrigin={{
            vertical: 75,
            horizontal: -8,
          }}
          handlePopoverOpen={handlePopoverOpen}
          handlePopoverClose={handlePopoverClose}
        />
        <MarginPopover
          anchorEl={gradientHover}
          anchorOrigin={{
            vertical: 10,
            horizontal: 'center',
          }}
          marginHighlight={gradientMarginHover}
          open={openGradientPopover}
          transformOrigin={{
            vertical: 'top',
            horizontal: 100,
          }}
          handlePopoverOpen={handleGradientPopoverOpen}
          handlePopoverClose={handleGradientPopoverClose}
        />

        <Info title={hasNewNetMargin ? 'Net margin old / new' : 'Net margin'}>
          <Box
            padding="0.6rem 0.3rem"
            display="inline-flex"
            justifyContent="left"
            alignItems="center"
            borderRadius="0.5rem"
            minWidth="5.25rem"
            color={netMarginThemeColor}
            sx={{ cursor: 'default' }}
            onMouseEnter={
              hasNewNetMargin ? undefined : handleNetMarginPopoverOpen
            }
            onMouseLeave={
              hasNewNetMargin ? undefined : handleNetMarginPopoverClose
            }
          >
            <MarginValues
              margin={netMargin}
              newMargin={newNetMargin}
              lineHeight={valueLineHeight}
              onZoneOpen={handleNetMarginPopoverOpen}
              onZoneClose={handleNetMarginPopoverClose}
            />
          </Box>

          <Box
            display="inline-flex"
            margin="0 0.375rem 0"
            borderRadius="0.125rem"
            height="0.25rem"
            sx={{
              '& :first-of-type(div)': {
                borderTopLeftRadius: '0.25rem',
                borderBottomLeftRadius: '0.25rem',
              },
              '& :last-of-type(div)': {
                borderTopRightRadius: '0.25rem',
                borderBottomRightRadius: '0.25rem',
              },
              backgroundImage: `linear-gradient(to right, ${sortedNetMarginHighlights
                .map((smh) => NetMarginHighlightColor[smh.type])
                .join(', ')})`,
              '&:hover': {
                transform: 'scaleY(1.5)',
                transition: 'all 0.15s ease-in-out',
              },
            }}
          >
            {sortedNetMarginHighlights.map((smh) => (
              <Box
                ref={netMarginGradientHover}
                key={smh.type}
                flex="1"
                onMouseEnter={() => handleGradientPopoverOpen(smh, true)}
                onMouseLeave={() => handleGradientPopoverClose(true)}
              />
            ))}
          </Box>
        </Info>

        <MarginPopover
          anchorEl={marginAnchor}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          marginHighlight={netMarginHighlight}
          open={openNetMarginPopover}
          transformOrigin={{
            vertical: 75,
            horizontal: -8,
          }}
          handlePopoverOpen={handleNetMarginPopoverOpen}
          handlePopoverClose={handleNetMarginPopoverClose}
          isNetMarginPopover={true}
        />
        <MarginPopover
          anchorEl={netMarginGradientHover}
          anchorOrigin={{
            vertical: 10,
            horizontal: 'center',
          }}
          marginHighlight={gradientMarginHover}
          open={openNetMarginGradientPopover}
          transformOrigin={{
            vertical: 'top',
            horizontal: 100,
          }}
          handlePopoverOpen={handleGradientPopoverOpen}
          handlePopoverClose={handleGradientPopoverClose}
          isNetMarginPopover={true}
        />
        {calculateMargins && (
          <Tooltip title="Calculate margins" placement="top">
            <IconButton onClick={calculateMargins}>
              <CustomIcon icon={IconName.Calculate} sx={{ color: '#3F8CFF' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
};

const NEW_MARGIN_DISCLAIMER =
  "Grey is the new margin: revenue invoiced so far plus forecast revenue (to year-end by default), minus actual and forecast costs. The colored old margin uses today's bill and pay rates, and the zone applies only to it.";

const MarginValues: React.FC<{
  margin: number;
  newMargin?: number;
  lineHeight?: string;
  onZoneOpen: () => void;
  onZoneClose: () => void;
}> = ({ margin, newMargin, lineHeight, onZoneOpen, onZoneClose }) => {
  const valueSx = { fontSize: '1.3rem', lineHeight };
  const infoIcon = (
    <InfoOutlinedIcon
      color="secondary"
      sx={{
        fontSize: '1.25rem',
        marginLeft: '0.4rem',
      }}
    />
  );

  if (newMargin == null) {
    return (
      <>
        <Typography variant="h3" sx={valueSx}>
          {margin}%
        </Typography>
        {infoIcon}
      </>
    );
  }

  return (
    <>
      <Box display="inline-flex" alignItems="baseline">
        <Typography
          variant="h3"
          sx={valueSx}
          onMouseEnter={onZoneOpen}
          onMouseLeave={onZoneClose}
        >
          {margin}%
        </Typography>
        <Typography
          variant="h3"
          color="text.secondary"
          sx={{ ...valueSx, marginLeft: '0.5rem' }}
        >
          {newMargin}%
        </Typography>
      </Box>
      <Tooltip title={NEW_MARGIN_DISCLAIMER} placement="top">
        {infoIcon}
      </Tooltip>
    </>
  );
};

interface Props {
  anchorEl: React.MutableRefObject<HTMLElement | null>;
  anchorOrigin: PopoverOrigin;
  marginHighlight: MarginHighlight | null;
  open: boolean;
  transformOrigin: PopoverOrigin;
  handlePopoverOpen: (
    hoveredBlock?: MarginHighlight,
    isNetMarginPopover?: boolean,
  ) => void;
  handlePopoverClose: (isNetMarginPopover?: boolean) => void;
  isNetMarginPopover?: boolean;
}

const MarginPopover: React.FC<Props> = ({
  anchorEl,
  anchorOrigin,
  marginHighlight,
  open,
  transformOrigin,
  handlePopoverOpen,
  handlePopoverClose,
  isNetMarginPopover = false,
}) => {
  const { header } = useMemo(() => {
    let header = `${marginHighlight?.min} .. ${marginHighlight?.max}%`;

    if (marginHighlight?.min == null) {
      header = `${marginHighlight?.max}% and below`;
    } else if (marginHighlight?.max == null) {
      header = `${marginHighlight?.min}% and above`;
    }

    return { header };
  }, [marginHighlight]);

  const themeColor: string = isNetMarginPopover
    ? NetMarginHighlightColor[marginHighlight?.type ?? '']
    : GrossMarginHighlightColor[marginHighlight?.type || ''] ?? 'text.primary';

  return (
    <Popover
      style={{ pointerEvents: 'none' }}
      open={open}
      anchorEl={anchorEl.current}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      PaperProps={{
        onMouseEnter: () => handlePopoverOpen(undefined, isNetMarginPopover),
        onMouseLeave: () => handlePopoverClose(isNetMarginPopover),
        sx: {
          pointerEvents: 'auto',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '1.5rem 1.5rem 2.5rem 1.5rem',
          gap: '1.5rem',
          borderTop: `0.25rem solid ${themeColor}`,
          width: isNetMarginPopover ? '18.75rem' : '22rem',
        },
      }}
    >
      <Box
        display="flex"
        padding="0 0.5rem"
        flexDirection="column"
        alignItems="flex-start"
      >
        <Typography variant="body2" color="text.secondary">
          {header}
        </Typography>
        <Typography variant="h3">
          {`${
            isNetMarginPopover && marginHighlight?.type
              ? NetMarginHighlightTitle[marginHighlight?.type]
              : `“${titleCase(
                  (marginHighlight?.type ?? '').toLowerCase(),
                )}” zone`
          }`}
        </Typography>
      </Box>
      {!isNetMarginPopover && (
        <>
          <Box
            display="flex"
            gap="0.75rem"
            padding="0 0.5rem"
            flexDirection="column"
            alignItems="flex-start"
            alignSelf="stretch"
          >
            <Typography color="text.secondary" fontSize="0.68rem">
              Possible actions
            </Typography>
            <List
              sx={{
                padding: '0 0 0 0.875rem',
                listStyle: 'disc',
                '& :last-child': {
                  marginBottom: 0,
                },
              }}
            >
              {(MarginHighlightActions[marginHighlight?.type ?? ''] ?? []).map(
                (action, index) => (
                  <ListItem
                    key={`margin-action-${index}`}
                    sx={{
                      display: 'list-item',
                      marginBottom: '0.75rem',
                      padding: 0,
                    }}
                  >
                    <Typography display="inline" variant="body1">
                      {action}
                    </Typography>
                  </ListItem>
                ),
              )}
            </List>
          </Box>
          <Box
            display="flex"
            padding="0 0.5rem"
            flexDirection="column"
            alignItems="flex-start"
            alignSelf="stretch"
          >
            <Typography color="text.secondary" fontSize="0.68rem">
              Learn more
            </Typography>
            <Link
              href={GrossMarginHandlingGuidelineDoc}
              variant="body1"
              target="_blank"
            >
              Gross margin handling guidelines↗
            </Link>
          </Box>
        </>
      )}
    </Popover>
  );
};
